/**
 * 📸🔗 Foto-Station + modulare Paketdienst-Anbindung (Vinted Go, DHL ServicePoint & Standard-Dienste)
 *
 * Wiederverwendung der bestehenden Handy-Betrieb-Logik (VintedErfassen, DhlErfassen, NewPackageModal,
 * turboPaketPilot) als saubere Untermodule für die Foto-/Mitarbeiter-Station:
 *  - Vorladen (vorladenStart für Vinted beim Öffnen des Erfassen-Modus)
 *  - Erkennen & Live-Buchen (vintedIntake / dhlIntake inkl. Erhalt des Etiketten-OCR-Namens bei DHL)
 *  - Einheitliche Fach-Berechnung über fachFuer (Nachname mit Unterfach, Vinted-Kurznummer V0–V9,
 *    Hausnummer mit Shop-Straßenschutz, Zusammenlegen gleicher Empfänger, Platzhalter-Schutz)
 *  - Nachlauf nach dem Speichern: Vinted-Beweisfoto im Hintergrund hochladen (vintedFoto),
 *    DHL-Namen im Hintergrund nachladen (dhlLookup / DHL_NACHLADEN_MS), Münchner DHL-USB-Stick-Queue.
 */
import { annahmeAktiv, vintedIntake, vintedFoto, vorladenStart, istPaketCode, vintedCode, autoFach, notiz } from './vinted';
import { lookupName, platzhalterName, normCode, isVintedSortOn, vintedFach, vintedCodeAusTracking, glsInVintedCode } from './vintedNames';
import {
  dhlAnnahmeAktiv, dhlIntake, dhlLookup, dhlNotiz, dhlRein, istDhlScanCode,
  dhlPlatzhalterName, DHL_NACHLADEN_MS, isDhlLabelMitNameOn,
} from './dhlBridge';
import {
  dpdAktiv, dpdAnnahmeAktiv, dpdAnnahme, dpdLookup, dpdNotiz,
  dpdCodeAusTracking, bekannterDpdName, vorladenStart as dpdVorladenStart,
} from './dpd';
import { isAutoRegalLetter, getCarrierSortMode, cleanName } from './printerService';
import { autoRegalFach } from './regalUnterfach';
import { fachFuer, istPlatzhalterName } from './turboPaketPilot';
import { bereinigeEmpfaengerName } from '../utils/packageMatchingHelpers';
import { mitWiederholung } from './serverWarte';
import { savePaket } from '../packageSyncHelper';
import { updatePackageCache } from './packageOperations';
import { isDhlStickEnabled, isPhotoSchnelleinlagerungEnabled, enqueueDhlStickSchnelleinlagerung } from './dhlUsbStick';

const AUTO_KEY = 'kiosk_station_auto_fach';
/** Schalter je Gerät: an der Foto-Station Pakete mit sicher berechnetem Fach SOFORT speichern (ohne Fach-QR-Scan). */
export const isStationAutoFachOn = () => { try { return localStorage.getItem(AUTO_KEY) === '1'; } catch { return false; } };
export const setStationAutoFachOn = (on) => { try { localStorage.setItem(AUTO_KEY, on ? '1' : '0'); } catch { /* */ } };

const offen = (p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt;
// Eindeutige DHL-Präfixe (3S ist in NL auch PostNL → nur mit „DHL" vom Etikett-Logo; 3299… = 24-stellige bpost/DHL-ServicePoint-NL-Sendung)
const DHL_EINDEUTIG = /^(JVGL|VGL|JJGL|CWGL|JSQL|JUN|IUN|JJN|JJD|JD\d|0034|0038|GM|3299\d{16})/i;

/**
 * 🧩 Untermodul 1: Vinted Go (Live-Annahme, Beweisfoto-Upload, Kurznummer/Nachname-Fach)
 */
export const vintedStationSubmodule = {
  id: 'vinted',
  isActive: () => annahmeAktiv(),
  preload: () => {
    if (annahmeAktiv()) {
      vorladenStart().catch(() => { /* */ });
    }
  },
  matchCode: ({ kandidaten, dienst }) => {
    if (!annahmeAktiv()) return '';
    return kandidaten.find((c) => istPaketCode(c))
      || (/vinted/i.test(dienst) ? (kandidaten.find((c) => glsInVintedCode(c, 'Vinted')) || '') : '');
  },
  intake: async ({ code, pakete, inFlight }) => {
    const kurz = normCode(code);
    const p = (pakete || []).find((x) => offen(x) && vintedCode(x) === kurz);
    if (p || (inFlight && inFlight.has(`vinted:${kurz}`))) {
      return { art: 'vinted', code, kurz, schonDa: true, paket: p || null };
    }
    const r = await mitWiederholung(() => vintedIntake(code));
    if (r.reason === 'fremd') return { art: 'vinted', code, kurz, fremd: true };
    const name = (r.ok && r.name) ? r.name : (lookupName(kurz) || platzhalterName(kurz));
    const istPh = istPlatzhalterName(name);
    const fachVorschlag = autoFach(kurz, name, {
      vintedSort: isVintedSortOn(),
      autoLetter: isAutoRegalLetter(),
      fachAusName: autoRegalFach,
      fachAusNummer: vintedFach,
    });
    return {
      art: 'vinted',
      code,
      kurz,
      ok: !!r.ok,
      gebucht: !!r.ok,
      intakeId: r.intakeId || null,
      name,
      nameVonDienst: !!(r.ok && r.name),
      istPlatzhalter: istPh,
      reason: r.reason || '',
      fachVorschlag,
      paketdienst: 'Vinted',
    };
  },
  afterSave: (anb, { previewDataUrl } = {}) => {
    if (!anb || !anb.ok || !previewDataUrl) return;
    // Wie in VintedErfassen.js: Paketfoto im Hintergrund an Vinted Go nachreichen (blockiert das Fließband nicht)
    const bild = previewDataUrl.startsWith('data:') ? previewDataUrl : `data:image/jpeg;base64,${previewDataUrl}`;
    mitWiederholung(() => vintedFoto(anb.code, anb.intakeId, bild))
      .then((fr) => {
        if (!fr || !fr.ok) {
          notiz('foto_paketpilot', anb.code, { ok: false, reason: (fr && fr.reason) || 'unbekannt', message: (fr && fr.message) || 'Foto-Station: Foto nicht hochgeladen' });
        }
      })
      .catch(() => { /* */ });
  },
};

/**
 * 🧩 Untermodul 2: DHL ServicePoint NL / DE (Live-Annahme, OCR-Namens-Erhalt, Hintergrund-Namensnachladen, USB-Stick)
 */
export const dhlStationSubmodule = {
  id: 'dhl',
  isActive: () => dhlAnnahmeAktiv(),
  preload: () => {},
  matchCode: ({ kandidaten, dienst }) => {
    if (!dhlAnnahmeAktiv()) return '';
    const istDhl = /^dhl/i.test(dienst);
    return kandidaten.map(dhlRein).find((c) => istDhlScanCode(c) && (istDhl || DHL_EINDEUTIG.test(c))) || '';
  },
  intake: async ({ code, pakete, ocrName, inFlight }) => {
    const p = (pakete || []).find((x) => offen(x) && dhlRein(x.trackingNumber) === code);
    if (p || (inFlight && inFlight.has(`dhl:${code}`))) {
      return { art: 'dhl', code, schonDa: true, paket: p || null };
    }
    const r = await mitWiederholung(() => dhlIntake(code));
    if (r.reason === 'abgelehnt') return { art: 'dhl', code, fremd: true, reason: r.message || 'abgelehnt' };
    const auto = (() => { const m = getCarrierSortMode('DHL'); return m ? m === 'nachname' : isAutoRegalLetter(); })();
    const portalName = (r.ok && r.name) ? bereinigeEmpfaengerName(cleanName(r.name)) : '';
    const sauberOcr = bereinigeEmpfaengerName(cleanName(ocrName || ''));
    const hatEchtenOcrNamen = !!(sauberOcr && !istPlatzhalterName(sauberOcr));
    const platzhalter = dhlPlatzhalterName(code);
    // Vorteil der Foto-Station: Steht der Empfängername bereits auf dem Etikett (wie auf DHL-NL-Labels),
    // behalten wir den OCR-Namen sofort, auch wenn das DHL-Portal den Namen erst Sekunden später liefert!
    const finalName = portalName || (hatEchtenOcrNamen ? sauberOcr : platzhalter);
    const istPh = !portalName && !hatEchtenOcrNamen;
    return {
      art: 'dhl',
      code,
      ok: !!r.ok,
      gebucht: !!r.ok && !r.schonImBestand,
      ohneName: !!(r.ok && !portalName),
      name: finalName,
      nameVonDienst: !!portalName,
      istPlatzhalter: istPh,
      reason: r.reason || '',
      autoNachname: auto,
      paketdienst: 'DHL',
      platzhalter,
    };
  },
  afterSave: (anb, { paket, getPakete, onUpdatePaket, onPrintPaket } = {}) => {
    if (!anb || !paket) return;
    // 1) Münchner DHL-USB-Stick (falls auf diesem Gerät aktiv): Schnelleinlagerung in die Queue geben
    try {
      if (isDhlStickEnabled() && isPhotoSchnelleinlagerungEnabled() && anb.code) {
        enqueueDhlStickSchnelleinlagerung({ barcodes: [anb.code], label: 'Foto-Station' });
      }
    } catch { /* */ }

    // 2) Niederlande DHL-ServicePoint: Portal liefert den Namen oft erst 5–135 s nach dem Scan.
    //    Wurde das Paket nur mit Platzhalter („DHL 123-456") angelegt, laden wir den Namen wie in DhlErfassen.js nach.
    if (anb.ohneName && anb.istPlatzhalter && anb.code) {
      const verzoegrtDrucken = isDhlLabelMitNameOn();
      const nachladen = (versuch) => {
        if (versuch >= DHL_NACHLADEN_MS.length) {
          // Nach Ablauf aller Versuche immer noch kein Name vom Portal → falls Druck auf Namen gewartet hat, jetzt mit Platzhalter drucken
          if (verzoegrtDrucken && typeof onPrintPaket === 'function') {
            const liste = (typeof getPakete === 'function' ? getPakete() : []) || [];
            const akt = liste.find((x) => x && x.id === paket.id);
            if (akt && offen(akt)) {
              try { onPrintPaket(akt); } catch { /* */ }
            }
          }
          return;
        }
        setTimeout(async () => {
          try {
            const listeVorab = (typeof getPakete === 'function' ? getPakete() : []) || [];
            const aktVorab = listeVorab.find((x) => x && x.id === paket.id);
            // Nicht mehr offen (schon ausgegeben/gelöscht) oder Name bereits manuell geändert → Nachladen beenden
            if (!aktVorab || !offen(aktVorab) || !istPlatzhalterName(aktVorab.name)) return;

            const l = await dhlLookup(anb.code);
            if (l && l.found && l.name) {
              const echterName = cleanName(l.name) || l.name.trim();
              const liste = (typeof getPakete === 'function' ? getPakete() : []) || [];
              const akt = liste.find((x) => x && x.id === paket.id);
              if (!akt || !offen(akt) || !istPlatzhalterName(akt.name)) return;
              const neuesFach = (akt.regalplatz && akt.regalplatz !== '?')
                ? akt.regalplatz
                : (fachFuer({
                  name: echterName,
                  streetAndNumber: akt.streetAndNumber || '',
                  trackingNumber: anb.code,
                  paketdienst: 'DHL',
                }, liste) || akt.regalplatz || '');
              const nowIso = new Date().toISOString();
              const aktualisiert = {
                ...akt,
                name: echterName,
                ...(neuesFach ? { regalplatz: neuesFach } : {}),
                updatedAt: nowIso,
                lastUpdated: Date.now(),
              };
              try { await savePaket(aktualisiert); } catch { /* */ }
              try { updatePackageCache(aktualisiert); } catch { /* */ }
              if (typeof onUpdatePaket === 'function') {
                try { onUpdatePaket(aktualisiert); } catch { /* */ }
              }
              if (typeof onPrintPaket === 'function' && verzoegrtDrucken) {
                try { onPrintPaket(aktualisiert); } catch { /* */ }
              }
              return;
            }
          } catch { /* nächster Versuch */ }
          nachladen(versuch + 1);
        }, DHL_NACHLADEN_MS[versuch]);
      };
      nachladen(0);
    }
  },
};

export const STATION_SUBMODULES = [vintedStationSubmodule, dhlStationSubmodule, {
  /**
   * 🧩 Untermodul 3: DPD / Pickup (Vorab-Namen aus SHIPPED/DELIVERED, optional Live-Annahme per DELIVERY-Event)
   */
  id: 'dpd',
  isActive: () => dpdAktiv(),
  preload: () => {
    if (dpdAktiv()) {
      dpdVorladenStart(false).catch(() => { /* */ });
    }
  },
  matchCode: ({ kandidaten, dienst }) => {
    if (!dpdAktiv()) return '';
    if (/^(vinted|dhl|mondial|inpost|gls|ups|postnl|bpost|hermes)/i.test(dienst)) return '';
    const istDpd = /^(dpd|pickup|chronopost)/i.test(dienst);
    for (const raw of kandidaten) {
      const code = dpdCodeAusTracking(raw);
      if (!code) continue;
      // Schutz vor fremden 14-stelligen Codes (Hermes, GTIN-14): Nur als DPD zuordnen, wenn Etikett DPD zeigt,
      // 27/28-stelliger DPD-Leitcode vorliegt oder die Paketnummer bereits im DPD-Pickup-Bestand angekündigt ist.
      if (istDpd || raw.replace(/\D/g, '').length >= 27 || bekannterDpdName(code)) {
        return code;
      }
    }
    return '';
  },
  intake: async ({ code, pakete, ocrName, inFlight }) => {
    const p = (pakete || []).find((x) => offen(x) && dpdCodeAusTracking(x.trackingNumber) === code);
    if (p || (inFlight && inFlight.has(`dpd:${code}`))) {
      return { art: 'dpd', code, schonDa: true, paket: p || null };
    }
    const sauberOcr = bereinigeEmpfaengerName(cleanName(ocrName || ''));
    const hatEchtenOcrNamen = !!(sauberOcr && !istPlatzhalterName(sauberOcr));
    const platzhalter = `DPD ${code.slice(-6, -3)}-${code.slice(-3)}`;

    if (dpdAnnahmeAktiv()) {
      const r = await mitWiederholung(() => dpdAnnahme(code));
      if (r && r.reason === 'fremd') return { art: 'dpd', code, fremd: true, reason: r.message || 'fremd' };
      const rawPickupName = (r && r.name) || bekannterDpdName(code) || '';
      const pickupName = rawPickupName ? bereinigeEmpfaengerName(cleanName(rawPickupName)) : '';
      const finalName = pickupName || (hatEchtenOcrNamen ? sauberOcr : platzhalter);
      const istPh = !pickupName && !hatEchtenOcrNamen;
      return {
        art: 'dpd',
        code,
        ok: !!(r && r.ok),
        gebucht: !!(r && r.ok && r.gebucht !== false),
        name: finalName,
        nameVonDienst: !!pickupName,
        istPlatzhalter: istPh,
        reason: (r && r.reason) || '',
        paketdienst: 'DPD',
        platzhalter,
      };
    }

    // Nur-Lesen-Modus: Namen aus Vorab-Cache (0 ms) oder per Lookup (~0,15 s) aus DPD Pickup ergänzen
    let rawPickupName = bekannterDpdName(code);
    if (!rawPickupName) {
      const l = await dpdLookup(code).catch(() => null);
      if (l && l.ok && l.found && l.name) rawPickupName = l.name;
    }
    const pickupName = rawPickupName ? bereinigeEmpfaengerName(cleanName(rawPickupName)) : '';
    const finalName = pickupName || (hatEchtenOcrNamen ? sauberOcr : '');
    if (!finalName) return null;
    return {
      art: 'dpd',
      code,
      ok: true,
      gebucht: false,
      nurLesen: true,
      name: finalName,
      nameVonDienst: !!pickupName,
      istPlatzhalter: false,
      reason: '',
      paketdienst: 'DPD',
      platzhalter,
    };
  },
}];

/**
 * Startet Vorlade-Aufgaben aller aktiven Station-Untermodule (z. B. Vinted-Annahme-Tab aufwärmen).
 */
export function vorladenFuerStation() {
  for (const mod of STATION_SUBMODULES) {
    try { if (mod.isActive()) mod.preload(); } catch { /* */ }
  }
}

/**
 * Prüft das Erkennungs-Ergebnis der Station über die registrierten Untermodule.
 * → null (nicht zuständig, weiter wie bisher) oder Anbindungs-Objekt des Untermoduls.
 */
export async function anbindungFuerStation({ tracking, codes, paketdienst, pakete, ocrName = '', inFlight = null }) {
  const kandidaten = [tracking, ...(Array.isArray(codes) ? codes : [])]
    .map((x) => String((x && (x.text || x.value)) || x || '').trim().toUpperCase().replace(/\s+/g, ''))
    .filter((x) => x.length >= 6);
  const dienst = String(paketdienst || '').trim();

  for (const mod of STATION_SUBMODULES) {
    const code = mod.matchCode({ kandidaten, dienst });
    if (code) {
      return mod.intake({ code, pakete, ocrName, inFlight });
    }
  }
  return null;
}

/**
 * Offline-/Import-Fallback für Vinted-Etiketten (wie im Handy-Betrieb NewPackageModal.js),
 * wenn die Live-Annahme aus ist: übernimmt gemerkten Namen aus vintedNames oder setzt „Vinted 123-456".
 */
export function wendeVintedOfflineFallbackAn(detected) {
  if (!detected) return;
  const vCode = vintedCodeAusTracking(detected.tracking) || glsInVintedCode(detected.tracking, detected.paketdienst);
  if (vCode) {
    if (!detected.paketdienst) detected.paketdienst = 'Vinted Go';
    if (!detected.name || !String(detected.name).trim()) {
      detected.name = lookupName(vCode) || platzhalterName(vCode);
    }
    return;
  }
  const dCode = dpdCodeAusTracking(detected.tracking);
  if (dCode) {
    const dName = bekannterDpdName(dCode);
    if (dName && (!detected.name || !String(detected.name).trim())) {
      detected.name = bereinigeEmpfaengerName(cleanName(dName)) || dName;
      if (!detected.paketdienst) detected.paketdienst = 'DPD';
    }
  }
}

/**
 * Einheitliche Regalfach-Berechnung für die Station — nutzt exakt dieselbe Logik wie der Handy-Betrieb
 * (`fachFuer` aus turboPaketPilot.js + `anb.fachVorschlag`).
 */
export function berechneStationFach(detected, anb = null, pakete = []) {
  if (!detected) return '';
  if (anb && anb.art === 'vinted' && anb.fachVorschlag) {
    return anb.fachVorschlag;
  }
  return fachFuer({
    name: detected.name || '',
    streetAndNumber: detected.street || '',
    trackingNumber: detected.tracking || '',
    paketdienst: detected.paketdienst || '',
  }, pakete) || (anb && anb.fachVorschlag) || '';
}

/** Nach dem Anlegen (oder Fehlschlag) ins Protokoll der Anbindung — wie „Vinted erfassen" / „DHL erfassen" / „DPD". */
export function protokollNachAnlegen(anb, { ok, fehler } = {}) {
  if (!anb || !anb.art || anb.schonDa || anb.fremd) return;
  if (anb.art === 'dpd') {
    dpdNotiz('annahme_paketpilot', anb.code, !!ok, '', ok ? (anb.nurLesen ? 'Foto-Station (Nur-Lesen)' : 'Foto-Station') : String(fehler || '').slice(0, 200));
    if (ok && !anb.ok && !anb.nurLesen) {
      dpdNotiz('rueckfall', anb.code, false, anb.reason || 'unbekannt', 'Foto-Station: in PaketPilot angelegt, bei DPD Pickup nicht bestätigt');
    }
    return;
  }
  const schreib = anb.art === 'vinted' ? notiz : dhlNotiz;
  schreib('annahme_paketpilot', anb.code, ok ? { ok: true, message: 'Foto-Station' } : { ok: false, message: String(fehler || '').slice(0, 200) });
  if (ok && !anb.ok) schreib('rueckfall', anb.code, { reason: anb.reason || 'unbekannt', message: `Foto-Station: in PaketPilot angelegt, bei ${anb.art === 'vinted' ? 'Vinted' : 'DHL'} nicht bestätigt` });
}

/**
 * Vollständiger Nachlauf nach erfolgreichem Speichern an der Station:
 * 1) Protokoll-Eintrag (protokollNachAnlegen)
 * 2) Modul-spezifischer Hintergrund-Nachlauf (Vinted-Beweisfoto, DHL-Namensnachladen, DHL-USB-Stick)
 */
export function nachSpeichernAnbindung(anb, ctx = {}) {
  if (!anb || !anb.art || anb.schonDa || anb.fremd) return;
  protokollNachAnlegen(anb, { ok: true });
  const mod = STATION_SUBMODULES.find((m) => m.id === anb.art);
  if (mod && typeof mod.afterSave === 'function') {
    try { mod.afterSave(anb, ctx); } catch { /* */ }
  }
}

